// Preview and test send for a red flag warning message.
//
// Both work on a message's TEXT (subject + body + red-flag number), saved or not, so the
// same pieces serve the Messages table's row buttons and the add/edit dialog. The server
// fills the text with invented sample data — no employee's details are ever shown here.
//
// The preview is the server's HTML shown inside a frame whose `sandbox` attribute is
// empty: no scripts, no forms, no navigation, no access to this page. It must stay
// exactly `sandbox=""` and the HTML must never be put into this page directly; a test
// holds both.

import { useEffect, useId, useState } from 'react';
import { Loader2 } from 'lucide-react';
import Modal from '../Modal';
import { ModalFormErrorBanner } from '../ModalFormErrorBanner';
import { InlineLoader } from '../loading';
import { redFlagWarningMailsAPI, type RedFlagWarningDraft, type RedFlagWarningStep } from '../../services/api';
import { SAMPLE_NOTE, apiMessage, flagNumberLabel, looksLikeAddress } from './warningMailText';

export interface WarningPreviewData {
  subject: string;
  html: string;
}

export function WarningPreviewFrame({ preview }: { preview: WarningPreviewData }) {
  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      <div className="border-b border-gray-200 bg-gray-50 px-3 py-2 text-sm">
        <span className="text-gray-500">Subject: </span>
        <span className="font-medium text-gray-900">{preview.subject}</span>
      </div>
      <iframe title="Mail preview" sandbox="" srcDoc={preview.html} className="h-96 w-full bg-white" />
    </div>
  );
}

/** "Send test to": one address, the message filled with sample data, nobody copied. */
export function WarningTestSend({ projectId, draft }: { projectId: number; draft: RedFlagWarningDraft | null }) {
  const inputId = useId();
  const [to, setTo] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);

  const send = async () => {
    const address = to.trim();
    if (!draft) {
      setResult({ ok: false, text: 'Choose a red flag number first.' });
      return;
    }
    if (!looksLikeAddress(address)) {
      setResult({ ok: false, text: 'Enter one e-mail address.' });
      return;
    }
    setSending(true);
    setResult(null);
    try {
      const res = await redFlagWarningMailsAPI.testSend(projectId, { ...draft, to: address });
      setResult({ ok: true, text: `Test sent to ${res.data?.data?.sentTo ?? address}.` });
    } catch (e) {
      setResult({ ok: false, text: apiMessage(e, 'Could not send the test. Please try again.') });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-1.5">
      <label htmlFor={inputId} className="block text-sm font-medium text-gray-700">Send test to</label>
      <div className="flex gap-2">
        <input
          id={inputId}
          type="email"
          value={to}
          onChange={(e) => { setTo(e.target.value); setResult(null); }}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            e.preventDefault();
            void send();
          }}
          placeholder="you@channelplay.in"
          className="form-input"
        />
        <button
          type="button"
          onClick={() => void send()}
          disabled={sending}
          className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
        >
          {sending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
          Send test
        </button>
      </div>
      <p className="text-xs text-gray-500">
        Sends this message, filled with sample data, to this one address. Nobody else is copied.
      </p>
      {result && (
        <p className={`text-sm ${result.ok ? 'text-green-700' : 'text-red-700'}`} role={result.ok ? 'status' : 'alert'}>
          {result.text}
        </p>
      )}
    </div>
  );
}

const draftOf = (step: RedFlagWarningStep): RedFlagWarningDraft => ({
  flagNumber: step.flagNumber,
  subjectTemplate: step.subjectTemplate,
  bodyTemplate: step.bodyTemplate,
});

/** Row button "Preview": the saved message as the employee would see it. `step` null = closed. */
export function WarningPreviewModal({
  projectId, step, onClose,
}: { projectId: number; step: RedFlagWarningStep | null; onClose: () => void }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<WarningPreviewData | null>(null);

  useEffect(() => {
    if (!step) return undefined;
    let alive = true;
    setLoading(true);
    setError(null);
    setPreview(null);
    redFlagWarningMailsAPI
      .preview(projectId, draftOf(step))
      .then((res) => {
        if (!alive) return;
        setPreview({ subject: res.data.data.subject, html: res.data.data.html });
      })
      .catch((e) => {
        if (alive) setError(apiMessage(e, 'Could not build the preview. Please try again.'));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [projectId, step]);

  return (
    <Modal
      isOpen={step !== null}
      onClose={onClose}
      title={step ? `Preview — ${flagNumberLabel(step.flagNumber)}` : 'Preview'}
      size="lg"
    >
      <div className="space-y-3">
        {loading && <InlineLoader label="Building the preview…" className="flex py-6" />}
        {error && <ModalFormErrorBanner message={error} />}
        {preview && (
          <>
            <WarningPreviewFrame preview={preview} />
            <p className="text-xs text-gray-500">{SAMPLE_NOTE}</p>
          </>
        )}
      </div>
    </Modal>
  );
}

/** Row button "Send test": the saved message to one address. `step` null = closed. */
export function WarningTestSendModal({
  projectId, step, onClose,
}: { projectId: number; step: RedFlagWarningStep | null; onClose: () => void }) {
  return (
    <Modal
      isOpen={step !== null}
      onClose={onClose}
      title={step ? `Send a test — ${flagNumberLabel(step.flagNumber)}` : 'Send a test'}
      size="md"
    >
      {step && <WarningTestSend key={step.id} projectId={projectId} draft={draftOf(step)} />}
    </Modal>
  );
}
